/**
 * Haptics vocabulary (WOW · feel). Native (Capacitor Haptics) gets the full set; the web only
 * vibrates for the big moments on devices that support it (never for every tap). Respects the
 * Settings → Haptics switch (feelPrefs) and reduced motion.
 */
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { isNative } from './platform';
import { useFeelPrefs } from './feelPrefs';
import { useApp } from '../state/store';

export type HapticKind = 'tap' | 'select' | 'step' | 'success' | 'celebrate' | 'warning' | 'error';

const WEB_PATTERN: Partial<Record<HapticKind, number | number[]>> = {
  success: 18,
  celebrate: [14, 60, 14, 60, 28],
  error: [30, 50, 30],
};

let last = 0;

export function buzz(kind: HapticKind = 'tap'): void {
  if (!useFeelPrefs.getState().haptics || useApp.getState().settings.reducedMotion) return;
  const now = Date.now();
  // Never stack taps faster than the motor can render them.
  if (kind === 'tap' && now - last < 60) return;
  last = now;
  if (!isNative) {
    const p = WEB_PATTERN[kind];
    if (p && typeof navigator !== 'undefined' && 'vibrate' in navigator && matchMedia?.('(pointer: coarse)').matches) {
      try {
        navigator.vibrate(p);
      } catch {
        /* ignore */
      }
    }
    return;
  }
  const impact = (style: ImpactStyle) => void Haptics.impact({ style }).catch(() => undefined);
  switch (kind) {
    case 'tap':
      impact(ImpactStyle.Light);
      break;
    case 'select':
      void Haptics.selectionChanged().catch(() => undefined);
      break;
    case 'step':
      impact(ImpactStyle.Medium);
      break;
    case 'success':
      void Haptics.notification({ type: NotificationType.Success }).catch(() => undefined);
      break;
    case 'warning':
      void Haptics.notification({ type: NotificationType.Warning }).catch(() => undefined);
      break;
    case 'error':
      void Haptics.notification({ type: NotificationType.Error }).catch(() => undefined);
      break;
    case 'celebrate':
      // A little drum-roll: heavy · medium · light · success.
      impact(ImpactStyle.Heavy);
      setTimeout(() => impact(ImpactStyle.Medium), 90);
      setTimeout(() => impact(ImpactStyle.Light), 180);
      setTimeout(() => void Haptics.notification({ type: NotificationType.Success }).catch(() => undefined), 320);
      break;
  }
}
